import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Mail, Download, ClipboardList, PackageSearch } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingWhatsApp from "./FloatingWhatsApp";
import useSeo from "../hooks/useSeo";
import { useEnquiry } from "../context/EnquiryContext";
import { buildWhatsAppUrl, buildMailto, downloadCataloguePdf, inr, WARRANTY_NOTE, STOCK_DESCRIPTION } from "../lib/catalogue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquiryListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addMany } = useEnquiry();

  useSeo({
    title: "Shared enquiry list | Kapatia Pvt Ltd",
    description: "A shared selection of pre-owned IT & office equipment from Kapatia Pvt Ltd. Enquire on WhatsApp or email.",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Tokens are CODE or CODE~QTY — preserve the requested quantity from the share link.
    const wanted = (params.get("codes") || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const [code, q] = t.split("~");
        return { code: code.trim(), qty: Math.max(1, parseInt(q, 10) || 1) };
      });
    if (!wanted.length) { setLoading(false); return; }
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((d) => {
        const all = Array.isArray(d) ? d : [];
        const byCode = new Map(all.filter((p) => p.code).map((p) => [p.code, p]));
        const picked = wanted
          .map((w) => { const p = byCode.get(w.code); return p ? { ...p, qty: w.qty, qtyRequested: w.qty } : null; })
          .filter(Boolean);
        setItems(picked);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App public-site">
      <Header />
      <main className="container py-10" style={{ minHeight: "60vh" }}>
        <Link to="/store" className="inline-flex items-center gap-1.5 font-heading text-[13.5px] font-bold" style={{ color: "var(--green-800)" }}>
          <ArrowLeft size={15} /> Back to store
        </Link>
        <h1 className="mt-4 font-heading font-semibold leading-[1.05]" style={{ color: "var(--green-950)", fontSize: "clamp(28px,5vw,44px)" }} data-testid="enquiry-page-heading">
          Shared <span style={{ color: "var(--orange-700)" }}>enquiry list</span>
        </h1>
        <p data-testid="enquiry-page-warranty" className="mt-2 inline-block rounded-md px-2.5 py-1 text-[12.5px] font-semibold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>
          {STOCK_DESCRIPTION} {WARRANTY_NOTE}
        </p>

        {loading ? (
          <p className="mt-10" style={{ color: "var(--muted)" }}>Loading selection…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 grid place-items-center gap-3 py-16 text-center" data-testid="enquiry-page-empty">
            <PackageSearch size={40} style={{ color: "var(--muted)" }} />
            <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No items in this shared list</p>
            <Link to="/store" className="font-heading font-bold" style={{ color: "var(--orange-700)" }}>Browse the store →</Link>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={buildWhatsAppUrl(items)} target="_blank" rel="noopener noreferrer" data-testid="enquiry-page-whatsapp"
                 data-item-codes={items.map((i) => i.code).filter(Boolean).join(",")} data-item-count={items.length}
                 className="kp-button inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading text-[14px] font-bold text-white transition-transform hover:-translate-y-0.5"
                 style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }}>
                <MessageCircle size={16} /> Enquire on WhatsApp
              </a>
              <a href={buildMailto(items)} data-testid="enquiry-page-email"
                 data-item-codes={items.map((i) => i.code).filter(Boolean).join(",")} data-item-count={items.length}
                 className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-6 py-3 font-heading text-[14px] font-bold transition-transform hover:-translate-y-0.5"
                 style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                <Mail size={16} /> Email enquiry
              </a>
              <button onClick={() => downloadCataloguePdf({ title: "Shared enquiry list", items })} data-testid="enquiry-page-download"
                      className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-6 py-3 font-heading text-[14px] font-bold transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                <Download size={16} /> Download PDF
              </button>
              <button onClick={() => addMany(items)} data-testid="enquiry-page-add-all"
                      className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-6 py-3 font-heading text-[14px] font-bold transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                <ClipboardList size={16} /> Add all to my list
              </button>
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border bg-white" style={{ borderColor: "var(--line)" }}>
              <ul>
                {items.map((p, idx) => (
                  <li key={p.id} data-testid={`enquiry-page-row-${p.id}`}
                      className="flex items-center justify-between gap-3 px-4 py-3" style={{ borderTop: idx ? "1px solid var(--line)" : "none" }}>
                    <div className="min-w-0">
                      {p.code && <span className="inline-block rounded px-1.5 py-0.5 text-[10.5px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>{p.code}</span>}
                      <Link to={`/product/${p.id}`} className="mt-1 block font-sans text-[14px] font-medium leading-snug hover:underline" style={{ color: "var(--ink)" }}>{p.name}</Link>
                      <span className="text-[12px] font-semibold uppercase" style={{ color: "var(--orange-800)" }}>{p.brand}</span>
                    </div>
                    <span className="shrink-0 text-right font-heading text-[15px] font-extrabold" style={{ color: "var(--orange-700)" }}>
                      {p.price_on_request ? "On request" : inr(p.price)}
                      {p.qty > 1 && (
                        <span className="mt-0.5 block text-[11.5px] font-bold" style={{ color: "var(--green-800)" }} data-testid={`enquiry-page-qty-${p.id}`}>Qty {p.qty}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
